import type { ReactElement } from "react";
import { useMemo } from "react";

import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import { createRangeChipItem } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/createRangeChipItem";

import { MAX_CHIPS_GROUP_AMOUNT } from "../../../constants";
import type FindIncomingTrafficFilter from "../../../models/IncomingTrafficFilter/IncomingTrafficFilter";
import type { IIncomingTrafficFilterDto } from "../../../types/leadsSearchFilters.dto";
import { formatNumber, formatPercent } from "../../../utils/numberFormatters";

const IncomingTrafficFilterResult = ({
    filter: { options },
    value,
    isExpanded,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<FindIncomingTrafficFilter>): ReactElement | null => {
    const handleClose = (filterKey: keyof IIncomingTrafficFilterDto) => {
        const mappedValue = { ...value };
        delete mappedValue[filterKey];

        onFilterUpdate(mappedValue);
        onFilterValueRemove?.(mappedValue);
    };

    const chipItems = useMemo(() => {
        return [
            createRangeChipItem("monthlyVisits", formatNumber, options, value, onGetLifecycleStatus),
            createRangeChipItem("avgMonthlyVisits", formatNumber, options, value, onGetLifecycleStatus),
            createRangeChipItem("uniqueVisitors", formatNumber, options, value, onGetLifecycleStatus),
            createRangeChipItem("totalPageViews", formatNumber, options, value, onGetLifecycleStatus),
            createRangeChipItem("mobileWebVisitsShare", formatPercent, options, value, onGetLifecycleStatus),
        ].filter((item): item is ChipItem => item !== null);
    }, [options, value, onGetLifecycleStatus]);

    if (!chipItems.length) {
        return null;
    }

    return (
        <FilterResultContainer>
            <LimitedChipItems
                items={chipItems}
                onDelete={handleClose}
                maxItems={MAX_CHIPS_GROUP_AMOUNT}
                hideActionButton={!isActionEnabled}
                isTabExpanded={isExpanded}
            />
        </FilterResultContainer>
    );
};

export default IncomingTrafficFilterResult;
